<template>
  <!-- ==================== 실시간 활동 피드 (단순화 — SURINP-208) ==================== -->
  <div :class="['feed-panel', { 'feed-panel--mobile': isMobile }]">
    <header class="feed-panel__header">
      <span class="feed-panel__live-indicator">
        <span class="feed-panel__live-dot"></span>
        {{ t('dashboard.feed.live') }}
      </span>
      <span class="feed-panel__refresh">{{ t('dashboard.feed.refresh30s') }}</span>
    </header>

    <div v-if="items.length > 0" class="feed-panel__table-wrap">
      <table class="feed-table">
        <thead>
          <tr>
            <th class="feed-table__th feed-table__th--time">{{ t('dashboard.feed.columns.time') }}</th>
            <th class="feed-table__th feed-table__th--status">{{ t('dashboard.feed.columns.status') }}</th>
            <th class="feed-table__th feed-table__th--partner">{{ t('dashboard.feed.columns.partner') }}</th>
            <th class="feed-table__th feed-table__th--customer">{{ t('dashboard.feed.columns.customer') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in items"
            :key="item.sourceId"
            class="feed-table__row"
            tabindex="0"
            role="button"
            @click="goDetail(item)"
            @keydown.enter="goDetail(item)"
            @keydown.space.prevent="goDetail(item)"
          >
            <td class="feed-table__td feed-table__td--time">{{ formatTime(item.timestamp) }}</td>
            <td class="feed-table__td feed-table__td--status">
              <StatusBadge class="feed-status" :tone="feedTypeTone(item.type)">
                {{ t(typeLabel(item.type)) }}
              </StatusBadge>
            </td>
            <td class="feed-table__td feed-table__td--partner">{{ item.partnerName || '-' }}</td>
            <td class="feed-table__td feed-table__td--customer">{{ item.customerName || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else-if="loading" class="feed-panel__state">
      {{ t('dashboard.feed.loading') }}
    </div>
    <div v-else class="feed-panel__state feed-panel__state--empty">
      {{ t('dashboard.feed.empty') }}
    </div>
  </div>
</template>

<script setup>
/* global defineProps */
import { onDeactivated, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import i18n from '@/locales/i18n'
import { useActivityFeedFetch } from '@/composables/useActivityFeedFetch'
import { getRepairRequestHistoryDetail } from '@/api/domain/repair'
import StatusBadge from '@/components/common/StatusBadge.vue'
import { feedTypeTone } from '@/constants/statusTone'

const t = i18n.global.t
const router = useRouter()

defineProps({
  isMobile: { type: Boolean, default: false }
})

const TYPE_LABELS = {
  RECEIPT: 'dashboard.feed.types.receipt',
  ASSIGN: 'dashboard.feed.types.assign',
  VISIT_PLAN: 'dashboard.feed.types.visitPlan',
  COMPLETE: 'dashboard.feed.types.complete',
  DELAY: 'dashboard.feed.types.delay'
}

const { items, loading } = useActivityFeedFetch()

function typeLabel(type) {
  return TYPE_LABELS[type] || 'dashboard.feed.types.other'
}

function formatTime(timestamp) {
  if (!timestamp) return ''
  try {
    const d = new Date(timestamp)
    if (Number.isNaN(d.getTime())) return ''
    const hh = String(d.getHours()).padStart(2, '0')
    const mm = String(d.getMinutes()).padStart(2, '0')
    return `${hh}:${mm}`
  } catch (_e) {
    return ''
  }
}

/** 상세 조회 순번 — 늦게 끝난 이전 행 조회가 마지막 선택이나 화면 이탈을 덮어쓰지 않게 */
let detailSeq = 0

// 대시보드를 떠나면(탭 전환·다른 화면 이동) 기다리던 상세 조회로 이동하지 않는다
onDeactivated(() => { detailSeq++ })
onUnmounted(() => { detailSeq++ })

/**
 * 피드 행 클릭 시 그 접수의 RepairRequestHistoryDetail 로 이동 (SURINP-835).
 * 상세 화면은 `$route.params.id/partnerId` 를 읽고 고객사 ID 로 증상·급지·출장비 목록을 부르는데,
 * 피드 응답에는 고객사 ID 가 없어 상세 조회로 얻은 뒤 파라미터 경로로 간다. 얻지 못하면 이동하지 않는다.
 * 조회를 기다리는 사이 다른 행을 누르면 마지막에 누른 행만 이동하고, 화면을 떠났으면 이동하지 않는다.
 *
 * @param {{sourceId: string}} item
 */
async function goDetail(item) {
  if (!item || !item.sourceId) return
  const seq = ++detailSeq
  try {
    const detail = await getRepairRequestHistoryDetail(item.sourceId)
    if (seq !== detailSeq) return
    if (!detail || detail.partnerId == null) {
      window.__APP_LOG__('[RealtimeActivityFeedPanel] partnerId 누락 — 상세 페이지 이동 불가', item.sourceId)
      return
    }
    router.push({
      name: '접수 이력 상세 파라미터',
      params: { id: String(item.sourceId), partnerId: String(detail.partnerId) }
    })
  } catch (e) {
    window.__APP_LOG__(e)
  }
}
</script>

<style scoped>
/* ========================================
   RealtimeActivityFeedPanel (SURINP-208)
   ======================================== */
.feed-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  min-height: 400px;
}

.feed-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.feed-panel__live-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: var(--color-gray-700, #2E333B);
  letter-spacing: 0.04em;
}

.feed-panel__live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-success, #2E7D5B);
  animation: feed-live-pulse 2s infinite;
}

@keyframes feed-live-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.feed-panel__refresh {
  font-size: 11px;
  color: var(--color-gray-500, #5C616B);
}

/* ===== Table ===== */
.feed-panel__table-wrap {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.feed-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;
}

.feed-table__th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--color-gray-50, #F3F4F6);
  color: var(--color-gray-700, #2E333B);
  font-weight: 700;
  font-size: 12px;
  text-align: left;
  padding: 8px 10px;
  border-bottom: 1px solid var(--color-gray-100, #E8EAED);
}

.feed-table__th--time { width: 64px; }
.feed-table__th--status { width: 80px; }

.feed-table__td {
  padding: 10px;
  border-bottom: 1px solid var(--color-gray-50, #F3F4F6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 0;
}

.feed-table__td--time {
  font-feature-settings: 'tnum';
  color: var(--color-gray-500, #5C616B);
  width: 64px;
}

.feed-table__td--status {
  width: 80px;
  overflow: visible;
}

.feed-table__row {
  cursor: pointer;
  transition: background var(--transition-fast, 0.15s ease);
}

.feed-table__row:hover,
.feed-table__row:focus-visible {
  background: var(--color-gray-50, #F3F4F6);
  outline: none;
}

/* ===== Status badge — 색은 StatusBadge 톤(SURINP-683), 여기서는 크기만 ===== */
.feed-status {
  padding: 2px 8px;
  letter-spacing: 0.04em;
}

/* ===== State ===== */
.feed-panel__state {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: var(--color-gray-500, #5C616B);
  padding: 32px 16px;
}

.feed-panel__state--empty {
  font-style: italic;
}

/* ===== Mobile (<768px) ===== */
@media (max-width: 767px) {
  .feed-panel {
    min-height: 300px;
  }
  .feed-table {
    font-size: 12px;
  }
  .feed-table__th {
    padding: 8px 8px;
  }
  /* 모바일 터치 타겟 보강 — 행 높이 약 40px 확보 (font 12 + padding 14*2) */
  .feed-table__td {
    padding: 14px 8px;
  }
  .feed-table__th--time,
  .feed-table__td--time {
    width: 56px;
  }
  .feed-table__th--status,
  .feed-table__td--status {
    width: 64px;
  }
  .feed-status {
    font-size: 10px;
    padding: 1px 6px;
  }
}
</style>
